/**
 * Apps row — launches native apps from the home page.
 *
 * This is an Android-only household, so the launcher knows three targets:
 *   - wallaceTv     : intent:// URL the Wallace-TV APK hands to startActivity
 *                     (uses the Android TV / Leanback package variant)
 *   - androidPhone  : intent:// URL with the phone-variant package; Chrome
 *                     resolves it to the installed app, falling back to the
 *                     Play Store listing if the app is missing
 *   - fallback      : https URL for any other browser (desktop / weird UA)
 */

interface AppLauncher {
  id: string;
  label: string;
  bg: string;
  fg: string;
  mark: string;
  targets: {
    wallaceTv?: string;
    androidPhone?: string;
    fallback: string;
  };
  note?: string;
}

const APPS: AppLauncher[] = [
  {
    id: "youtube",
    label: "YouTube",
    bg: "bg-[#FF0000]",
    fg: "text-white",
    mark: "▶",
    targets: {
      wallaceTv: "intent://#Intent;package=com.google.android.youtube.tv;end",
      androidPhone: "intent://www.youtube.com/#Intent;package=com.google.android.youtube;scheme=https;end",
      fallback: "https://www.youtube.com/",
    },
  },
  {
    id: "netflix",
    label: "Netflix",
    bg: "bg-[#141414] border border-[#E50914]",
    fg: "text-[#E50914]",
    mark: "N",
    targets: {
      wallaceTv: "intent://#Intent;package=com.netflix.ninja;end",
      androidPhone: "intent://www.netflix.com/#Intent;package=com.netflix.mediaclient;scheme=https;end",
      fallback: "https://www.netflix.com/",
    },
  },
  {
    id: "crunchyroll",
    label: "Crunchyroll",
    bg: "bg-[#F47521]",
    fg: "text-white",
    mark: "C",
    targets: {
      // Crunchyroll uses the same package on Android TV and phone.
      wallaceTv: "intent://#Intent;package=com.crunchyroll.crunchyroid;end",
      androidPhone: "intent://www.crunchyroll.com/#Intent;package=com.crunchyroll.crunchyroid;scheme=https;end",
      fallback: "https://www.crunchyroll.com/",
    },
  },
  {
    id: "curiosity_stream",
    label: "Curiosity",
    bg: "bg-[#231F20] border border-[#00CFE8]",
    fg: "text-[#00CFE8]",
    mark: "CS",
    targets: {
      wallaceTv: "intent://#Intent;package=com.curiositystream.tv;end",
      androidPhone: "intent://www.curiositystream.com/#Intent;package=com.curiositystream;scheme=https;end",
      fallback: "https://www.curiositystream.com/",
    },
    note: "package name may need verifying — if launch fails open the Play Store",
  },
  {
    id: "premium_club",
    label: "Premium Club",
    bg: "bg-vermilion",
    fg: "text-goldHi",
    mark: "PC",
    targets: {
      wallaceTv: "intent://#Intent;package=com.premiumclub.androidtv;end",
      fallback: "https://premiumclub.tv/",
    },
    note: "package name may need updating",
  },
];

type Platform = "wallaceTv" | "androidPhone" | "desktop";

function detectPlatform(): Platform {
  const ua = navigator.userAgent;
  if (/WallaceTV\//.test(ua)) return "wallaceTv";
  if (/Android/i.test(ua)) return "androidPhone";
  return "desktop";
}

export function AppLaunchRow(): JSX.Element {
  const platform = detectPlatform();

  function launch(app: AppLauncher): void {
    const direct =
      platform === "wallaceTv"    ? app.targets.wallaceTv :
      platform === "androidPhone" ? app.targets.androidPhone :
      undefined;

    if (!direct) {
      // Desktop or no native target — just open the web app in a new tab.
      window.open(app.targets.fallback, "_blank", "noopener");
      return;
    }

    if (platform === "wallaceTv") {
      // The APK's WebViewClient intercepts intent:// and starts the activity.
      // No fallback needed — if it isn't installed the APK toasts a hint.
      window.location.href = direct;
      return;
    }

    // Android phone: Chrome handles intent:// URLs natively, including
    // routing to Play Store when the target package isn't installed. No
    // visibility watchdog needed — the OS makes the decision.
    window.location.href = direct;
  }

  const caption =
    platform === "wallaceTv"    ? "Jump to a native app on this TV" :
    platform === "androidPhone" ? "Opens the app on your phone" :
                                  "Opens the web version of each app";

  return (
    <section className="mb-10">
      <div className="flex items-baseline justify-between mb-3">
        <div className="flex items-baseline gap-3 flex-wrap">
          <div className="section-label">Apps</div>
          <div className="text-parchment/60 text-xs">{caption}</div>
        </div>
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-4">
        {APPS.map((app) => (
          <button
            key={app.id}
            onClick={() => launch(app)}
            className="group flex flex-col items-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-goldHi rounded-lg p-2"
            title={app.note ? `${app.label} — ${app.note}` : app.label}
          >
            <div
              className={`${app.bg} ${app.fg} aspect-square w-full max-w-[120px] rounded-2xl flex items-center justify-center font-display text-3xl shadow-lg transition-transform duration-200 ease-spring group-hover:scale-[1.06]`}
            >
              {app.mark}
            </div>
            <div className="mt-2 text-ivory text-sm font-display tracking-smallcaps">
              {app.label}
            </div>
          </button>
        ))}
      </div>
    </section>
  );
}
